import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Input,
  Text,
  Textarea,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdCalendarToday,
  MdCheckCircle,
  MdClose,
  MdEventRepeat,
  MdPersonOff,
  MdSchedule,
  MdTimer,
} from 'react-icons/md';
import Card from 'components/card/Card';
import CustomModal from 'components/modal/Modal';
import { AgendaCalendar } from 'components/calendar/Calendar';
import { Appointment, EstadoCita, Genero, TipoCita } from 'types/models';

// ─── Mock availability (config ya guardada por el profesional) ────────────────

const MOCK_AVAILABILITY = {
  monday:    { enabled: true,  periods: [{ startTime: '08:00', endTime: '12:00' }, { startTime: '14:00', endTime: '17:00' }] },
  tuesday:   { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  wednesday: { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  thursday:  { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  friday:    { enabled: true,  periods: [{ startTime: '08:00', endTime: '13:00' }] },
  saturday:  { enabled: false, periods: [] },
  sunday:    { enabled: false, periods: [] },
};

// ─── Mock patients ──────────────────────────────────────────────────────────

function makePatient(id: string, nombres: string, apellidos: string, cedula: string) {
  return { id, cedula, nombres, apellidos, genero: Genero.OTRO, email: null, telefono: null };
}

function isoToday(hour: number, minute = 0) {
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

// ─── Mock appointments para "hoy" ──────────────────────────────────────────

const MOCK_APPOINTMENTS: Appointment[] = [
  {
    id: '1',
    patientId: 'p1',
    patient: makePatient('p1', 'Carlos', 'Mendoza', '1712345678'),
    professionalId: 'prof1',
    scheduledAt: isoToday(8, 0),
    durationMinutes: 30,
    tipoCita: TipoCita.PRIMERA_VEZ,
    estado: EstadoCita.CONFIRMADA,
    motivo: 'Dolor lumbar',
    notas: null,
    motivoCancelacion: null,
    episodeId: null,
    sessionPaymentId: null,
    sessionId: null,
    treatmentPlanId: null,
    sessionInfo: null,
    reprogramadaDeId: null,
    nuevaCitaId: null,
    motivoReprogramacion: null,
    createdAt: isoToday(0),
    updatedAt: isoToday(0),
  },
  {
    id: '2',
    patientId: 'p2',
    patient: makePatient('p2', 'María', 'Vélez', '0923456789'),
    professionalId: 'prof1',
    scheduledAt: isoToday(9, 0),
    durationMinutes: 45,
    tipoCita: TipoCita.SEGUIMIENTO,
    estado: EstadoCita.CONFIRMADA,
    motivo: 'Rehabilitación rodilla — sesión 4/10',
    notas: null,
    motivoCancelacion: null,
    episodeId: null,
    sessionPaymentId: null,
    sessionId: 's1',
    treatmentPlanId: 'plan1',
    sessionInfo: { numeroSesion: 4, totalSessions: 10 },
    reprogramadaDeId: null,
    nuevaCitaId: null,
    motivoReprogramacion: null,
    createdAt: isoToday(0),
    updatedAt: isoToday(0),
  },
  {
    id: '3',
    patientId: 'p3',
    patient: makePatient('p3', 'Jorge', 'Salazar', '1798765432'),
    professionalId: 'prof1',
    scheduledAt: isoToday(10, 30),
    durationMinutes: 30,
    tipoCita: TipoCita.INTERCONSULTA,
    estado: EstadoCita.COMPLETADA,
    motivo: 'Evaluación postural',
    notas: 'Paciente respondió bien al tratamiento.',
    motivoCancelacion: null,
    episodeId: null,
    sessionPaymentId: null,
    sessionId: null,
    treatmentPlanId: null,
    sessionInfo: null,
    reprogramadaDeId: null,
    nuevaCitaId: null,
    motivoReprogramacion: null,
    createdAt: isoToday(0),
    updatedAt: isoToday(0),
  },
  {
    id: '4',
    patientId: 'p4',
    patient: makePatient('p4', 'Ana', 'Torres', '1755566677'),
    professionalId: 'prof1',
    scheduledAt: isoToday(11, 0),
    durationMinutes: 30,
    tipoCita: TipoCita.SEGUIMIENTO,
    estado: EstadoCita.CANCELADA,
    motivo: 'Control mensual',
    notas: null,
    motivoCancelacion: 'Paciente enfermo',
    episodeId: null,
    sessionPaymentId: null,
    sessionId: null,
    treatmentPlanId: null,
    sessionInfo: null,
    reprogramadaDeId: null,
    nuevaCitaId: null,
    motivoReprogramacion: null,
    createdAt: isoToday(0),
    updatedAt: isoToday(0),
  },
];

const ESTADO_CONFIG: Record<EstadoCita, { colorScheme: string; label: string; icon: React.ElementType }> = {
  [EstadoCita.CONFIRMADA]: { colorScheme: 'blue', label: 'Confirmada', icon: MdCalendarToday },
  [EstadoCita.CANCELADA]: { colorScheme: 'red', label: 'Cancelada', icon: MdClose },
  [EstadoCita.COMPLETADA]: { colorScheme: 'green', label: 'Completada', icon: MdCheckCircle },
  [EstadoCita.REPROGRAMADA]: { colorScheme: 'purple', label: 'Reprogramada', icon: MdEventRepeat },
  [EstadoCita.NO_ASISTIO]: { colorScheme: 'gray', label: 'No asistió', icon: MdPersonOff },
};

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

// ─── Card de cita con acciones de cambio de estado ─────────────────────────

interface AppointmentRowProps {
  appointment: Appointment;
  onChangeEstado: (id: string, estado: EstadoCita) => void;
  onReprogramar: (appointment: Appointment) => void;
}

function AppointmentRow({ appointment, onChangeEstado, onReprogramar }: AppointmentRowProps) {
  const bg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('secondaryGray.500', 'secondaryGray.400');

  const estadoCfg = ESTADO_CONFIG[appointment.estado];
  const isConfirmada = appointment.estado === EstadoCita.CONFIRMADA;

  return (
    <Box bg={bg} border="1px solid" borderColor={borderColor} borderRadius="14px" overflow="hidden">
      <Flex px="14px" py="12px" align="center" gap="10px">
        <Flex direction="column" align="center" justify="center" w="48px" flexShrink={0}>
          <Icon as={MdSchedule} w="14px" h="14px" color={mutedColor} />
          <Text fontSize="xs" fontWeight="700" color={textColor}>{fmtTime(appointment.scheduledAt)}</Text>
        </Flex>

        <Box flex={1} minW="0">
          <Flex align="center" gap="6px" mb="2px" flexWrap="wrap">
            <Text fontSize="sm" fontWeight="700" color={textColor} noOfLines={1}>
              {appointment.patient.nombres} {appointment.patient.apellidos}
            </Text>
            <Badge colorScheme={estadoCfg.colorScheme} borderRadius="full" px="7px" fontSize="10px" fontWeight="700">
              <Icon as={estadoCfg.icon} w="9px" h="9px" mr="3px" />
              {estadoCfg.label}
            </Badge>
          </Flex>
          <Flex align="center" gap="3px" color={mutedColor}>
            <Icon as={MdTimer} w="11px" h="11px" />
            <Text fontSize="xs">{appointment.durationMinutes} min</Text>
            {appointment.motivo && (
              <Text fontSize="xs" noOfLines={1}>· {appointment.motivo}</Text>
            )}
          </Flex>
        </Box>
      </Flex>

      {isConfirmada && (
        <Flex gap="6px" px="14px" pb="12px" flexWrap="wrap">
          <Button size="xs" colorScheme="green" onClick={() => onChangeEstado(appointment.id, EstadoCita.COMPLETADA)}>
            Completar
          </Button>
          <Button size="xs" colorScheme="purple" variant="outline" onClick={() => onReprogramar(appointment)}>
            Reprogramar
          </Button>
          <Button size="xs" colorScheme="orange" variant="outline" onClick={() => onChangeEstado(appointment.id, EstadoCita.NO_ASISTIO)}>
            No asistió
          </Button>
          <Button size="xs" colorScheme="red" variant="outline" onClick={() => onChangeEstado(appointment.id, EstadoCita.CANCELADA)}>
            Cancelar
          </Button>
        </Flex>
      )}
    </Box>
  );
}

// ─── Modal reprogramar (preview — sin backend) ─────────────────────────────

interface ReprogramarPayload {
  scheduledAt: string;
  motivo: string;
}

interface ReprogramarModalProps {
  appointment: Appointment;
  onClose: () => void;
  onConfirm: (id: string, payload: ReprogramarPayload) => void;
}

function formatDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ReprogramarModal({ appointment, onClose, onConfirm }: ReprogramarModalProps) {
  const [scheduledAt, setScheduledAt] = useState(formatDatetimeLocal(appointment.scheduledAt));
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!scheduledAt) {
      setError('La nueva fecha es requerida');
      return;
    }
    const payload: ReprogramarPayload = {
      scheduledAt: new Date(scheduledAt).toISOString(),
      motivo,
    };
    console.log('Reprogramar payload:', payload);
    onConfirm(appointment.id, payload);
    onClose();
  };

  return (
    <CustomModal
      isOpen
      onClose={onClose}
      title="Reprogramar cita"
      primaryBtnLabel="Reprogramar"
      onPrimaryClick={handleSubmit}
    >
      <FormControl isInvalid={!!error} mb="16px">
        <FormLabel fontSize="sm" fontWeight="bold">
          Nueva fecha y hora <Text as="span" color="red.400">*</Text>
        </FormLabel>
        <Input
          type="datetime-local"
          value={scheduledAt}
          onChange={e => { setScheduledAt(e.target.value); setError(''); }}
          borderRadius="12px"
        />
        {error && <FormErrorMessage>{error}</FormErrorMessage>}
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm" fontWeight="bold">Motivo</FormLabel>
        <Textarea
          value={motivo}
          onChange={e => setMotivo(e.target.value)}
          placeholder="Ej: Paciente solicita cambio por motivos laborales"
          rows={3}
          borderRadius="12px"
        />
      </FormControl>
    </CustomModal>
  );
}

// ─── Vista principal ────────────────────────────────────────────────────────

export default function AppointmentSchedulingPreview() {
  const [appointments, setAppointments] = useState<Appointment[]>(MOCK_APPOINTMENTS);
  const [reprogramarTarget, setReprogramarTarget] = useState<Appointment | null>(null);

  const handleChangeEstado = (id: string, estado: EstadoCita) => {
    setAppointments(prev => prev.map(a => (a.id === id ? { ...a, estado } : a)));
  };

  const handleConfirmReprogramar = (id: string, payload: { scheduledAt: string; motivo: string }) => {
    setAppointments(prev => prev.map(a => (a.id === id
      ? { ...a, estado: EstadoCita.REPROGRAMADA, scheduledAt: payload.scheduledAt, motivoReprogramacion: payload.motivo || a.motivoReprogramacion }
      : a)));
  };

  const handleCreateClick = () => {
    console.log('Crear cita clickeado');
  };

  const handleSelectDate = (date: Date) => {
    console.log('Fecha seleccionada:', date);
  };

  const cardBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  return (
    <Box p="20px" minH="100vh">
      <Flex justify="space-between" align="center" mb="20px" flexWrap="wrap" gap="12px">
        <Heading as="h1" size="xl">
          Agendamiento de citas preview
        </Heading>
        <Button
          leftIcon={<Icon as={MdAdd} />}
          colorScheme="brand"
          onClick={handleCreateClick}
        >
          Crear cita
        </Button>
      </Flex>

      <Flex gap="20px" align="flex-start" wrap="wrap">
        <Box flex="3" minW="600px">
          <AgendaCalendar
            onSelectDate={handleSelectDate}
            availability={MOCK_AVAILABILITY}
            cardWidth="100%"
            cardHeight="80vh"
          />
        </Box>

        <Box flex="1" minW="320px">
          <Card p="16px" borderRadius="14px" bg={cardBg}>
            <Heading as="h2" size="md" mb="14px">
              Citas de hoy
            </Heading>
            <Flex direction="column" gap="10px">
              {appointments.map(appt => (
                <AppointmentRow
                  key={appt.id}
                  appointment={appt}
                  onChangeEstado={handleChangeEstado}
                  onReprogramar={setReprogramarTarget}
                />
              ))}
            </Flex>
          </Card>
        </Box>
      </Flex>

      {reprogramarTarget && (
        <ReprogramarModal
          appointment={reprogramarTarget}
          onClose={() => setReprogramarTarget(null)}
          onConfirm={handleConfirmReprogramar}
        />
      )}
    </Box>
  );
}
